import { VPS_RPC_CONFIG } from './rpcConfig';

type RPCResult = Record<string, unknown>;

// Ensure URLs end with /rpc if they don't already
const normalizeRpcUrl = (url: string) => {
  if (!url) return '';
  // Remove trailing slash first
  const cleanUrl = url.endsWith('/') ? url.slice(0, -1) : url;
  // Add /rpc if not already present
  return cleanUrl.endsWith('/rpc') ? cleanUrl : `${cleanUrl}/rpc`;
};

const PRIMARY_URL = normalizeRpcUrl(VPS_RPC_CONFIG.ravencoin.primary);
const FALLBACK_URL = normalizeRpcUrl(VPS_RPC_CONFIG.ravencoin.fallback);
const AUTH_HEADER = `Basic ${btoa(`${VPS_RPC_CONFIG.ravencoin.username}:${VPS_RPC_CONFIG.ravencoin.password}`)}`;

console.log('[RVN RPC] Config:', { 
  PRIMARY_URL, 
  FALLBACK_URL, 
  hasAuth: !!AUTH_HEADER,
  primaryRaw: VPS_RPC_CONFIG.ravencoin.primary,
  fallbackRaw: VPS_RPC_CONFIG.ravencoin.fallback
});

// Timeout for RPC calls (10 seconds)
const RPC_TIMEOUT = 10000;

// JSON-RPC 2.0 batch request support
async function callRpcBatch(requests: Array<{ method: string; params: unknown[]; id: number }>): Promise<Array<{ id: number; result?: RPCResult; error?: any }>> {
  async function send(url: string, headers: Record<string, string>) {
    const requestBody = requests.map(req => ({
      jsonrpc: '2.0',
      id: req.id,
      method: req.method,
      params: req.params,
    }));
    
    console.log(`[RVN RPC] Batch call: ${requests.length} requests to ${url}`);
    
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(`Batch RPC timeout after ${RPC_TIMEOUT}ms`)), RPC_TIMEOUT);
    });
    
    const fetchPromise = fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    const response = await Promise.race([fetchPromise, timeoutPromise]);

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error');
      throw new Error(`RPC batch ${url} responded with HTTP ${response.status}: ${errorText.substring(0, 200)}`);
    }

    const responseText = await response.text();
    const data = JSON.parse(responseText);
    
    // Batch responses come back as array
    if (Array.isArray(data)) {
      return data;
    }
    
    // Single response (shouldn't happen but handle it)
    return [data];
  }

  try {
    return await send(PRIMARY_URL, {
      'Content-Type': 'application/json',
      ...(PRIMARY_URL.includes('ting.finance') ? {} : (AUTH_HEADER ? { Authorization: AUTH_HEADER } : {})),
    });
  } catch (error) {
    console.warn(`[RVN RPC] Primary batch failed, trying fallback:`, error);
    if (!FALLBACK_URL) {
      throw error;
    }
    try {
      return await send(FALLBACK_URL, {
        'Content-Type': 'application/json',
        ...(AUTH_HEADER ? { Authorization: AUTH_HEADER } : {}),
      });
    } catch (fallbackError) {
      console.error(`[RVN RPC] Both batch endpoints failed:`, fallbackError);
      throw fallbackError;
    }
  }
}

async function callRpc(method: string, params: unknown[]): Promise<RPCResult> {
  async function send(url: string, headers: Record<string, string>) {
    const requestBody = {
      jsonrpc: '2.0',
      id: 1,
      method,
      params,
    };
    
    console.log(`[RVN RPC] Calling ${method} on ${url}`, { params });
    
    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(`RPC timeout after ${RPC_TIMEOUT}ms`)), RPC_TIMEOUT);
    });
    
    // Create fetch promise
    const fetchPromise = fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    // Race between fetch and timeout
    const response = await Promise.race([fetchPromise, timeoutPromise]);

    console.log(`[RVN RPC] Response status: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error');
      console.error(`[RVN RPC] HTTP Error ${response.status}:`, errorText.substring(0, 500));
      throw new Error(`RPC ${url} responded with HTTP ${response.status}: ${errorText.substring(0, 200)}`);
    }

    const responseText = await response.text();
    console.log(`[RVN RPC] Response text (first 500 chars):`, responseText.substring(0, 500));
    
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error(`[RVN RPC] JSON parse error:`, parseError, 'Response:', responseText.substring(0, 200));
      throw new Error(`Invalid JSON response from RPC: ${responseText.substring(0, 200)}`);
    }
    
    if (data.error) {
      console.error(`[RVN RPC] RPC error:`, data.error);
      throw new Error(data.error?.message ?? `RPC error: ${JSON.stringify(data.error)}`);
    }
    
    console.log(`[RVN RPC] Success - result keys:`, Object.keys(data.result || {}).slice(0, 10));
    return data.result ?? {};
  }

  try {
    return await send(PRIMARY_URL, {
      'Content-Type': 'application/json',
      // Don't send auth for public endpoints (ting.finance doesn't need it)
      ...(PRIMARY_URL.includes('ting.finance') ? {} : (AUTH_HEADER ? { Authorization: AUTH_HEADER } : {})),
    });
  } catch (error) {
    console.warn(`[RVN RPC] Primary failed, trying fallback:`, error);
    if (!FALLBACK_URL) {
      throw error; // No fallback available
    }
    try {
      return await send(FALLBACK_URL, {
        'Content-Type': 'application/json',
        // Try with auth for VPS endpoint
        ...(AUTH_HEADER ? { Authorization: AUTH_HEADER } : {}),
      });
    } catch (fallbackError) {
      console.error(`[RVN RPC] Both primary and fallback failed:`, fallbackError);
      throw fallbackError;
    }
  }
}

export interface RavencoinRpcAssetMetadata {
  name: string;
  amount: number;
  units: number;
  reissuable: number;
  has_ipfs?: number;
  ipfs_hash?: string;
  owner?: string;
}

export async function fetchWalletAssetBalances(
  walletAddress: string
): Promise<Record<string, number>> {
  try {
    console.log(`[RVN RPC] Fetching asset balances for: ${walletAddress}`);
    const result = await callRpc('listassetbalancesbyaddress', [walletAddress]);
    const balances = (result as Record<string, number>) || {};
    console.log(`[RVN RPC] Found ${Object.keys(balances).length} assets`);
    return balances;
  } catch (error) {
    console.error('[RVN RPC] Failed to fetch balances:', error);
    // Return empty object instead of throwing - allows UI to show "no assets" instead of infinite spinner
    return {};
  }
}

// Metadata cache to prevent duplicate RPC calls
const assetMetadataCache = new Map<string, { metadata: RavencoinRpcAssetMetadata | null; timestamp: number }>();
const METADATA_CACHE_TTL = 60000; // 1 minute

// Request deduplication - track in-flight requests
const inFlightRequests = new Map<string, Promise<RavencoinRpcAssetMetadata | null>>();

// Batch fetch metadata using JSON-RPC 2.0 batch requests (ONE HTTP call instead of 210!)
export async function fetchAssetMetadataBatch(
  assetNames: string[],
  batchSize: number = 100 // JSON-RPC batch size (most servers handle 100+ requests)
): Promise<Map<string, RavencoinRpcAssetMetadata | null>> {
  const now = Date.now();
  const results = new Map<string, RavencoinRpcAssetMetadata | null>();
  const toFetch: Array<{ assetName: string; id: number }> = [];
  
  // Check cache first
  let requestId = 1;
  for (const assetName of assetNames) {
    const cached = assetMetadataCache.get(assetName);
    if (cached && (now - cached.timestamp) < METADATA_CACHE_TTL) {
      results.set(assetName, cached.metadata);
    } else {
      toFetch.push({ assetName, id: requestId++ });
    }
  }
  
  if (toFetch.length === 0) {
    return results;
  }
  
  // Fetch in JSON-RPC 2.0 batches (single HTTP request per batch)
  for (let i = 0; i < toFetch.length; i += batchSize) {
    const batch = toFetch.slice(i, i + batchSize);
    const batchRequests = batch.map(({ assetName, id }) => ({
      method: 'getassetdata',
      params: [assetName],
      id,
    }));
    
    try {
      const batchResponses = await callRpcBatch(batchRequests);
      
      // Map responses back to asset names
      batchResponses.forEach((response) => {
        const batchItem = batch.find(item => item.id === response.id);
        if (!batchItem) return;
        
        const assetName = batchItem.assetName;
        let metadata: RavencoinRpcAssetMetadata | null = null;
        
        if (response.error) {
          metadata = null;
        } else if (response.result && typeof response.result === 'object') {
          metadata = response.result as RavencoinRpcAssetMetadata;
        }
        
        // Cache result
        assetMetadataCache.set(assetName, { metadata, timestamp: now });
        results.set(assetName, metadata);
      });
    } catch (error) {
      console.error(`[RVN RPC] Batch fetch failed, falling back to individual calls:`, error);
      // Fallback to individual calls if batch fails
      for (const { assetName } of batch) {
        try {
          const metadata = await fetchAssetMetadata(assetName);
          results.set(assetName, metadata);
        } catch {
          results.set(assetName, null);
        }
      }
    }
  }
  
  console.log(`[RVN RPC] Batch fetched ${toFetch.length} assets in ${Math.ceil(toFetch.length / batchSize)} HTTP requests`);
  return results;
}

export async function fetchAssetMetadata(
  assetName: string
): Promise<RavencoinRpcAssetMetadata | null> {
  // Check cache first
  const now = Date.now();
  const cached = assetMetadataCache.get(assetName);
  if (cached && (now - cached.timestamp) < METADATA_CACHE_TTL) {
    return cached.metadata;
  }
  
  // Check if request is already in flight (deduplication)
  const inFlight = inFlightRequests.get(assetName);
  if (inFlight) {
    return inFlight;
  }
  
  // Create new request
  const requestPromise = (async () => {
    try {
      const result = await callRpc('getassetdata', [assetName]);
      const metadata = (!result || typeof result !== 'object') ? null : (result as RavencoinRpcAssetMetadata);
      assetMetadataCache.set(assetName, { metadata, timestamp: now });
      return metadata;
    } catch (error) {
      const nullMetadata = null;
      assetMetadataCache.set(assetName, { metadata: nullMetadata, timestamp: now });
      return nullMetadata;
    } finally {
      // Remove from in-flight after completion
      inFlightRequests.delete(assetName);
    }
  })();
  
  inFlightRequests.set(assetName, requestPromise);
  return requestPromise;
}

export function extractIpfsHash(metadata?: string | null): string | undefined {
  if (!metadata) return undefined;

  if (metadata.startsWith('ipfs://')) {
    return metadata.slice('ipfs://'.length).trim();
  }

  if (metadata.startsWith('IPFS: ')) {
    return metadata.slice('IPFS: '.length).trim();
  }

  if (metadata.startsWith('IPFS:')) {
    return metadata.slice('IPFS:'.length).trim();
  }

  // Check if it's a bare IPFS hash (Qm... or bafy...)
  const ipfsHashPattern = /^(Qm[1-9A-HJ-NP-Za-km-z]{44}|baf[a-z0-9]{56,})$/;
  if (ipfsHashPattern.test(metadata.trim())) {
    return metadata.trim();
  }

  return undefined;
}

export function ipfsMetadataToGateway(metadata?: string | null): string | undefined {
  if (!metadata) return undefined;
  const hash = extractIpfsHash(metadata);
  if (hash) {
    return `https://cloudflare-ipfs.com/ipfs/${hash}`;
  }
  if (metadata.startsWith('https://') || metadata.startsWith('http://')) {
    return metadata;
  }
  return undefined;
}

/**
 * Fetch IPFS JSON metadata for a given hash.
 * Returns parsed JSON or null if unavailable/invalid.
 */
export async function fetchIpfsMetadata(ipfsHash: string): Promise<Record<string, any> | null> {
  if (!ipfsHash) return null;
  const url = `https://cloudflare-ipfs.com/ipfs/${ipfsHash}`;
  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('IPFS fetch timeout')), 2000);
    });

    const fetchPromise = fetch(url, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });

    const response = await Promise.race([fetchPromise, timeoutPromise]);
    if (!response.ok) return null;

    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  } catch {
    return null;
  }
}

/**
 * Check if IPFS hash points to JSON metadata and extract image URL
 * Returns the direct URL if it's an image, or extracts image URL from JSON if it's metadata
 * Has a 2-second timeout to prevent hanging
 */
async function getImageUrlFromIpfs(ipfsHash: string): Promise<string> {
  const directUrl = `https://cloudflare-ipfs.com/ipfs/${ipfsHash}`;
  
  try {
    // Create a timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('IPFS fetch timeout')), 2000);
    });
    
    // Try to fetch and check if it's JSON (with timeout)
    const fetchPromise = fetch(directUrl, {
      method: 'GET',
      headers: { 'Accept': 'application/json, image/*' },
    });
    
    const response = await Promise.race([fetchPromise, timeoutPromise]);
    
    if (!response.ok) {
      return directUrl;
    }
    
    const contentType = response.headers.get('content-type') || '';
    const text = await response.text();
    
    // Check if it's JSON (by content-type or by trying to parse)
    let isJson = contentType.includes('application/json');
    let json: any = null;
    
    if (isJson || text.trim().startsWith('{') || text.trim().startsWith('[')) {
      try {
        json = JSON.parse(text);
      } catch {
        // Not valid JSON, treat as image
        return directUrl;
      }
    } else {
      // Not JSON, return direct URL
      return directUrl;
    }
    
    // Extract image URL from JSON - check multiple possible fields
    const imageUrl = json.image || json.image_url || json.imageUrl || json.imageURI || 
                     json.uri || json.url || json.media || json.media_url || 
                     json.thumbnail || json.thumbnail_url;
    
    if (imageUrl) {
      let finalUrl = imageUrl;
      
      // Handle IPFS protocol
      if (finalUrl.startsWith('ipfs://')) {
        const hash = finalUrl.replace('ipfs://', '').trim().split('/')[0];
        return `https://cloudflare-ipfs.com/ipfs/${hash}`;
      }
      
      // Handle IPFS hash in path format
      if (finalUrl.includes('/ipfs/')) {
        const match = finalUrl.match(/\/ipfs\/([Qmb][a-zA-Z0-9]{43,})/);
        if (match) {
          return `https://cloudflare-ipfs.com/ipfs/${match[1]}`;
        }
      }
      
      // If it's already a full URL, use it
      if (finalUrl.startsWith('http://') || finalUrl.startsWith('https://')) {
        return finalUrl;
      }
      
      // If it's a bare IPFS hash, convert to gateway URL
      const hashPattern = /^([Qmb][a-zA-Z0-9]{43,})/;
      const hashMatch = finalUrl.trim().match(hashPattern);
      if (hashMatch) {
        return `https://cloudflare-ipfs.com/ipfs/${hashMatch[1]}`;
      }
    }
    
    // If JSON but no image field found, fall back to direct URL
    return directUrl;
  } catch (error) {
    // On error (including timeout), fall back to direct URL
    return directUrl;
  }
}

export type RavencoinNFTResult = {
  assetName: string;
  creator: string;
  description: [] | [string];
  imageUrl: [] | [string];
};

/**
 * SIMPLE, DIRECT NFT FETCH - NO LOOPS, NO BATCHING, NO COMPLEXITY
 */
export async function fetchRavencoinNFTsDirectRPC(
  walletAddress: string
): Promise<RavencoinNFTResult[]> {
  console.log(`[RVN NFT FUNC] ========== FUNCTION CALLED ==========`);
  console.log(`[RVN NFT FUNC] walletAddress: ${walletAddress}`);
  
  try {
    console.log(`[RVN NFT] ========== STARTING DIRECT RPC FETCH ==========`);
    console.log(`[RVN NFT] Wallet: ${walletAddress}`);
    
    // Step 1: Get all asset balances
    const balances = await fetchWalletAssetBalances(walletAddress);
    
    if (!balances || Object.keys(balances).length === 0) {
      console.log(`[RVN NFT] ❌ No assets found for ${walletAddress}`);
      return [];
    }

    const totalAssets = Object.keys(balances).length;
    console.log(`[RVN NFT] ✅ Found ${totalAssets} assets, checking for NFTs...`);

    // Step 2: Fetch metadata using JSON-RPC 2.0 batch requests (ONE HTTP call instead of 210!)
    const assetNames = Object.keys(balances);
    const metadataMap = await fetchAssetMetadataBatch(assetNames, 100); // 100 requests per batch (JSON-RPC standard)
    const metadataResults = assetNames.map(assetName => ({
      assetName,
      data: metadataMap.get(assetName) || null
    }));
    console.log(`[RVN NFT] ✅ Fetched metadata for ${metadataResults.length} assets (JSON-RPC batch)`);

    // Step 3: Filter NFTs INSTANTLY - no IPFS fetching, just use hash directly
    // IPFS image resolution happens lazily on the frontend for instant UI
    const nfts: RavencoinNFTResult[] = [];
    
    for (const { assetName, data: assetData } of metadataResults) {
      if (!assetData) continue;

      const ipfsHash = assetData.ipfs_hash;
      if (ipfsHash && typeof ipfsHash === 'string' && ipfsHash.trim()) {
        const trimmedHash = ipfsHash.trim();
        // Use IPFS gateway directly - frontend will handle JSON metadata resolution
        const ipfsUrl = `https://dweb.link/ipfs/${trimmedHash}`;
        
        nfts.push({
          assetName,
          creator: assetData.owner || walletAddress,
          description: [assetData.owner || `IPFS: ${trimmedHash}`] as [string],
          imageUrl: [ipfsUrl] as [string],
        });
      }
    }
    
    console.log(`[RVN NFT] ========== FETCH COMPLETE ==========`);
    console.log(`[RVN NFT] ✅✅✅ Total NFTs found: ${nfts.length} ✅✅✅`);
    return nfts;
  } catch (error) {
    console.error('[RVN NFT] ❌❌❌ CRITICAL ERROR in fetchRavencoinNFTsDirectRPC:', error);
    return [];
  }
}
